import React, { useRef } from 'react';
import { createContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useSetAtom } from 'jotai';
import { type LoggedInUser, type UserContextValue } from './types';
import { useRelayEnvironment } from 'react-relay';
import { attemptClientLogin, cleanupClientAuthFlow, hasAutomaticAuthFailed } from './auth-flow';
import { useFlags } from '@atlaskit/flag/use-flags';
import { UserStaffIdAtom } from '../ui/state/LabsAtoms';
import { queryUserProfile } from './utils';
import { initializeFeatureGates } from '../feature-flags';

export const UserContext: React.Context<UserContextValue> =
	createContext<UserContextValue>(undefined);

type UserProviderProps = {
	children: React.ReactNode;
};

/**
 * UserProvider manages the authentication state and user profile data.
 *
 * Authentication Flow:
 * 1. On mount, attempts to fetch user profile data
 * 2. If profile data is incomplete or fetch fails, triggers handleAuthFailure
 * 3. handleAuthFailure will:
 *    - If automatic auth fails, show a flag to user to manually clear cookies
 *
 * The heartbeat component also triggers handleAuthFailure if session becomes invalid.
 */
export function UserProvider({ children }: UserProviderProps): React.JSX.Element {
	const [identity, setIdentity] = useState<LoggedInUser>({
		id: '',
		picture: '',
		accountId: '',
		name: '',
		nickname: '',
		email: '',
		staffId: '',
		isAtlassianEmployee: false,
	});
	const loginRedirectFailed = useRef<boolean>(false);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const environment = useRelayEnvironment();
	const { showFlag } = useFlags();
	const setUserStaffId = useSetAtom(UserStaffIdAtom);

	const handleAuthFailure = useCallback(async () => {
		if (hasAutomaticAuthFailed()) {
			loginRedirectFailed.current = true;
			return;
		}
		attemptClientLogin();
	}, []);

	const fetchUserProfile = useCallback(async () => {
		const res = await queryUserProfile(environment);
		if (res.success) {
			const { identity } = res;
			setIdentity(identity);
			// Update the atom with the user's staffId for query naming
			setUserStaffId(identity.staffId);
			// Initialize feature gates after user profile is loaded
			await initializeFeatureGates(identity.accountId);
		} else {
			const { error } = res;
			console.error('Failed to fetch user profile:', error);
			setError(true);
			await handleAuthFailure();
		}
	}, [environment, setUserStaffId, handleAuthFailure]);

	useEffect(() => {
		fetchUserProfile().finally(() => setLoading(false));
	}, [fetchUserProfile]);

	useEffect(() => {
		if (!loginRedirectFailed.current) {
			return;
		}

		// For whatever reason, attempting to redirect to login has failed and we need
		// to provide a way for the user to manually resolve their issue.
		showFlag({
			id: 'redirection-failed',
			title: 'Automatic login has failed',
			description: 'Please clear your cookies and try again, or use incognito mode.',
		});

		cleanupClientAuthFlow();
	}, [loginRedirectFailed, showFlag]);

	const value = useMemo(
		() => ({
			identity,
			loading,
			error,
			handleAuthFailure,
		}),
		[identity, loading, error, handleAuthFailure],
	);

	return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}
